import { useEffect, useRef, useState } from 'react'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { FlagIcon, type CountryCode } from './FlagIcon'

export interface LocaleOption {
  /** BCP-47-ish tag for languages, ISO code for countries. */
  id: string
  label: string
  flag: CountryCode
}

/**
 * A listbox rather than a `<select>`, because each row carries a flag and an
 * `<option>` cannot hold markup. That means owning the keyboard contract:
 * Up/Down move the active row, Enter/Space commit it, Escape closes without
 * committing, Home/End jump to the ends.
 */
export function LocaleDropdown({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: LocaleOption[]
  value: string
  onChange: (id: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(() => Math.max(0, options.findIndex((o) => o.id === value)))
  const wrapRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const selected = options.find((o) => o.id === value) ?? options[0]

  // Reopening starts from the committed row, not wherever the last hover left off.
  useEffect(() => {
    if (open) setActive(Math.max(0, options.findIndex((o) => o.id === value)))
  }, [open, options, value])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Keep the active row in view when arrowing past the 240px cap.
  useEffect(() => {
    if (!open) return
    const row = listRef.current?.children[active] as HTMLElement | undefined
    // Feature-detected: jsdom does not implement scrollIntoView, and the list
    // has to work under test as well as in a browser.
    row?.scrollIntoView?.({ block: 'nearest' })
  }, [open, active])

  const commit = (i: number) => {
    onChange(options[i].id)
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => Math.min(options.length - 1, i + 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => Math.max(0, i - 1))
        break
      case 'Home':
        e.preventDefault()
        setActive(0)
        break
      case 'End':
        e.preventDefault()
        setActive(options.length - 1)
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        commit(active)
        break
      case 'Escape':
        // Swallow it: closing the list must not also close the modal.
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        break
      default:
        break
    }
  }

  const labelId = `wh-locale-label-${label.replace(/\s+/g, '-').toLowerCase()}`

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span id={labelId} style={{ fontSize: 14, fontWeight: 500, color: COLORS.onSurfaceVariant }}>
        {label}
      </span>
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={labelId}
          onClick={() => setOpen((o) => !o)}
          onKeyDown={onKeyDown}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            height: 50,
            padding: '12px 16px',
            boxSizing: 'border-box',
            background: COLORS.surfaceContainerLowest,
            border: `1px solid ${COLORS.outlineVariant}`,
            borderRadius: 6,
            color: COLORS.onSurface,
            fontSize: 16,
            textAlign: 'left',
            cursor: 'pointer',
            transition: 'border-color 0.15s',
          }}
        >
          <FlagIcon code={selected.flag} />
          <span style={{ flex: 1, minWidth: 0 }}>{selected.label}</span>
          <Icon name="chevron-down" size={18} color={COLORS.onSurfaceVariant} />
        </button>

        {open && (
          <ul
            ref={listRef}
            role="listbox"
            aria-labelledby={labelId}
            tabIndex={-1}
            style={{
              position: 'absolute',
              top: 50,
              left: 0,
              right: 0,
              marginTop: 4,
              padding: '4px 0',
              maxHeight: 240,
              overflowY: 'auto',
              listStyle: 'none',
              zIndex: 10,
              background: COLORS.surfaceContainerLowest,
              border: `1px solid ${COLORS.outlineVariant}`,
              borderRadius: 6,
              boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
            }}
          >
            {options.map((o, i) => {
              const isSelected = o.id === value
              const isActive = i === active
              return (
                <li
                  key={o.id}
                  role="option"
                  aria-selected={isSelected}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => commit(i)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    height: 40,
                    padding: '8px 16px',
                    boxSizing: 'border-box',
                    fontSize: 16,
                    cursor: 'pointer',
                    // The site fills the selected row solid; using the brand
                    // primary rather than its blue keeps the modal on-palette
                    // and legible in both themes.
                    background: isSelected ? COLORS.primary : isActive ? COLORS.surfaceContainerHigh : 'transparent',
                    color: isSelected ? COLORS.onPrimary : COLORS.onSurface,
                  }}
                >
                  <FlagIcon code={o.flag} />
                  <span>{o.label}</span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
